import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import GuideHub from "@/components/guide/GuideHub";
import GuideChat from "@/components/guide/GuideChat";
import { getMode, modeToProjectType } from "@/components/guide/guideModes";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderPlus } from "lucide-react";

export default function AIBusinessGuide() {
  const navigate = useNavigate();
  const [view, setView] = useState("hub");
  const [activeMode, setActiveMode] = useState(null);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [deleteConvoId, setDeleteConvoId] = useState(null);

  const [saveOpen, setSaveOpen] = useState(false);
  const [pendingContent, setPendingContent] = useState("");
  const [pendingConvo, setPendingConvo] = useState(null);
  const [newProjTitle, setNewProjTitle] = useState("");

  const loadLists = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.get("/guide/conversations"), api.get("/guide/projects")]);
      setConversations(c.data || []);
      setProjects(p.data || []);
    } catch (e) { toast.error("Could not load your Guide history. Please refresh."); }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  const openMode = (key) => { setActiveMode(getMode(key)); setActiveConversationId(null); setView("chat"); };
  const openConversation = (convo) => { setActiveMode(getMode(convo.mode || "ask")); setActiveConversationId(convo.id); setView("chat"); };
  const backToHub = () => { setView("hub"); setActiveConversationId(null); loadLists(); };

  const deleteConversation = async () => {
    try {
      await api.delete(`/guide/conversations/${deleteConvoId}`);
      setConversations((prev) => prev.filter((c) => c.id !== deleteConvoId));
      toast.success("Conversation removed.");
    } catch (e) { toast.error("Could not remove conversation."); }
    finally { setDeleteConvoId(null); }
  };

  const requestSave = (content, convoId) => { setPendingContent(content); setPendingConvo(convoId); setSaveOpen(true); };

  const saveToProject = async (projectId) => {
    try {
      await api.post("/guide/items", {
        project_id: projectId,
        kind: modeToProjectType[activeMode?.key] || "draft",
        title: pendingContent.trim().split("\n").find((l) => l.trim())?.replace(/[*#>]/g, "").trim().slice(0, 80) || "Saved draft",
        content: pendingContent, mode: activeMode?.key || "ask", source_conversation_id: pendingConvo,
      });
      toast.success("Saved to project.");
      setSaveOpen(false); setNewProjTitle(""); loadLists();
    } catch (e) { toast.error("Could not save."); }
  };

  const createAndSave = async () => {
    if (!newProjTitle.trim()) { toast.error("Give the project a name."); return; }
    try {
      const r = await api.post("/guide/projects", { title: newProjTitle.trim(), category: "General" });
      await saveToProject(r.data.id);
    } catch (e) { toast.error("Could not create project."); }
  };

  return (
    <div>
      {view === "hub" ? (
        <>
          <PageHeader
            testid="guide-header"
            title="AI Business Guide"
            subtitle="Your integrated content strategist, creative partner, editor, product architect and business advisor, grounded in your business and goals."
          />
          <GuideHub
            conversations={conversations}
            projects={projects}
            onOpenMode={openMode}
            onOpenConversation={openConversation}
            onOpenProject={(p) => navigate(`/projects?open=${p.id}`)}
            onViewAllProjects={() => navigate("/projects")}
            onNewProject={() => navigate("/projects?new=1")}
            onDeleteConversation={setDeleteConvoId}
          />
        </>
      ) : (
        <GuideChat
          mode={activeMode}
          conversationId={activeConversationId}
          onConversationCreated={() => loadLists()}
          onBack={backToHub}
          onSaveNeedsProject={requestSave}
        />
      )}

      {/* Save to project */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="guide-save-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Save to a project</DialogTitle></DialogHeader>
          <DialogDescription>Keep this with related work so the Guide can build on it over time.</DialogDescription>
          <div className="space-y-1.5 py-2 max-h-56 overflow-y-auto">
            {projects.length === 0 && <p className="text-sm text-muted-foreground">No projects yet. Create one below.</p>}
            {projects.map((p) => (
              <button key={p.id} data-testid={`guide-save-to-${p.id}`} onClick={() => saveToProject(p.id)}
                className="w-full text-left px-4 py-3 rounded-2xl bg-muted/60 hover:bg-muted transition-colors">
                <p className="text-sm font-medium">{p.title}</p>
                <p className="text-xs text-muted-foreground">{p.category}</p>
              </button>
            ))}
          </div>
          <div className="pt-3 border-t border-border space-y-2">
            <Label>Or create a new project</Label>
            <div className="flex gap-2">
              <Input value={newProjTitle} onChange={(e) => setNewProjTitle(e.target.value)} placeholder="Project name" className="rounded-xl" data-testid="guide-new-project-name" />
              <Button onClick={createAndSave} data-testid="guide-create-save-project" className="rounded-full bg-primary hover:bg-primary/90 gap-1.5 shrink-0"><FolderPlus className="h-4 w-4" /> Create</Button>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setSaveOpen(false)} className="rounded-full">Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConvoId} onOpenChange={(o) => !o && setDeleteConvoId(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove this conversation?</AlertDialogTitle>
            <AlertDialogDescription>This conversation will be permanently removed from your account.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={deleteConversation} data-testid="guide-confirm-delete-conversation" className="rounded-full bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
